// src/components/finance/EMICalculator.jsx
import React, { useState } from 'react';

const EMICalculator = ({ carPrice }) => {
  const [activeTab, setActiveTab] = useState('emi');
  const [employmentType, setEmploymentType] = useState('salaried');
  const [loanAmount, setLoanAmount] = useState(
    carPrice ? Math.round(parseFloat(carPrice) * 0.8) : 500000
  );
  const [duration, setDuration] = useState(60);
  const [interestRate, setInterestRate] = useState(10);
  const [monthlyIncome, setMonthlyIncome] = useState(50000);
  const [annualITR, setAnnualITR] = useState(600000);
  const [runningEMI, setRunningEMI] = useState('');

  const calculateEMI = () => {
    const r = interestRate / 12 / 100;
    const n = duration;
    if (r === 0) return Math.round(loanAmount / n);
    const emi =
      (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return Math.round(emi);
  };

  const calculateMaxLoan = () => {
    const income =
      employmentType === 'salaried' ? monthlyIncome : annualITR / 12;
    const existingEMI = parseFloat(runningEMI) || 0;
    const maxEMI = income * 0.65 - existingEMI;
    const r = interestRate / 12 / 100;
    const n = duration;
    if (r === 0) return Math.round(maxEMI * n);
    const maxLoan =
      (maxEMI * (Math.pow(1 + r, n) - 1)) / (r * Math.pow(1 + r, n));
    return Math.max(0, Math.round(maxLoan));
  };

  const emi = calculateEMI();
  const totalPayable = emi * duration;
  const totalInterest = totalPayable - loanAmount;
  const maxLoanAmount = calculateMaxLoan();
  const r = interestRate / 12 / 100;
  const n = duration;
  const maxEMI =
    r === 0
      ? Math.round(maxLoanAmount / n)
      : Math.round(
          (maxLoanAmount * r * Math.pow(1 + r, n)) /
            (Math.pow(1 + r, n) - 1)
        );

  const principalPct = totalPayable > 0 ? (loanAmount / totalPayable) * 100 : 80;
  const interestPct = 100 - principalPct;

  // Donut chart math
  const R = 60;
  const cx = 80;
  const cy = 80;
  const circumference = 2 * Math.PI * R;
  const principalDash = (principalPct / 100) * circumference;
  const interestDash = (interestPct / 100) * circumference;

  const fmtINR = (n) =>
    '₹' + Math.round(n).toLocaleString('en-IN');

  const sliderStyle = (val, min, max) => {
    const pct = ((val - min) / (max - min)) * 100;
    return {
      width: '100%',
      height: 8,
      borderRadius: 4,
      outline: 'none',
      WebkitAppearance: 'none',
      appearance: 'none',
      cursor: 'pointer',
      background: `linear-gradient(to right, #003566 0%, #003566 ${pct}%, #dde3ec ${pct}%, #dde3ec 100%)`,
    };
  };

  return (
    <div className="emi-calc-wrap">
      <style>{`
        .emi-calc-wrap {
          margin: 32px 0;
          font-family: inherit;
        }
        .emi-calc-card {
          background: linear-gradient(160deg, #fffbe6 0%, #fff9f0 100%);
          border-radius: 20px;
          padding: 32px 28px;
          box-shadow: 0 8px 32px rgba(0,53,102,0.10);
          border: 1px solid #ffe9a0;
        }
        .emi-calc-title {
          text-align: center;
          font-size: 1.5rem;
          font-weight: 800;
          color: #003566;
          margin-bottom: 24px;
          letter-spacing: -0.3px;
        }
        /* ── Tabs ── */
        .emi-tabs {
          display: flex;
          background: #fff;
          border-radius: 12px;
          padding: 5px;
          margin-bottom: 28px;
          border: 1px solid #e8e8e8;
          gap: 4px;
        }
        .emi-tab {
          flex: 1;
          padding: 11px 8px;
          border: none;
          border-radius: 9px;
          font-size: 0.92rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          background: transparent;
          color: #777;
        }
        .emi-tab.active {
          background: #003566;
          color: #FFD60A;
          box-shadow: 0 2px 8px rgba(0,53,102,0.25);
        }
        /* ── Two-col layout ── */
        .emi-body {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 28px;
          align-items: start;
        }
        @media (max-width: 700px) {
          .emi-body { grid-template-columns: 1fr; gap: 20px; }
          .emi-calc-card { padding: 22px 16px; border-radius: 16px; }
          .emi-calc-title { font-size: 1.2rem; margin-bottom: 18px; }
        }
        /* ── Left panel ── */
        .emi-left {}
        .emi-monthly-label { font-size: 0.85rem; color: #888; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
        .emi-monthly-amount { font-size: 2.4rem; font-weight: 900; color: #003566; line-height: 1; margin-bottom: 4px; }
        .emi-monthly-sub { font-size: 0.8rem; color: #888; margin-bottom: 20px; }
        /* Donut */
        .emi-donut-row { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
        .emi-donut-svg { flex-shrink: 0; }
        .emi-legend { display: flex; flex-direction: column; gap: 12px; }
        .emi-legend-item {}
        .emi-legend-dot-row { display: flex; align-items: center; gap: 8px; }
        .emi-legend-dot { width: 11px; height: 11px; border-radius: 3px; flex-shrink: 0; }
        .emi-legend-label { font-size: 0.78rem; color: #888; }
        .emi-legend-val { font-size: 0.95rem; font-weight: 800; color: #111; margin-left: 19px; margin-top: 2px; }
        .emi-disclaimer {
          margin-top: 16px;
          font-size: 0.75rem;
          color: #aaa;
          line-height: 1.6;
          border-top: 1px solid #eedca0;
          padding-top: 12px;
        }
        /* Max loan panel */
        .emi-maxloan-badge {
          background: #fff;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #e8e8e8;
          text-align: center;
          margin-bottom: 16px;
        }
        .emi-maxloan-amount { font-size: 2rem; font-weight: 900; color: #003566; }
        .emi-maxloan-label { font-size: 0.82rem; color: #888; margin-top: 4px; }
        .emi-maxemi-row { display: flex; justify-content: space-between; align-items: center; background: #f0f4ff; border-radius: 10px; padding: 14px 18px; margin-bottom: 12px; }
        .emi-maxemi-lbl { font-size: 0.85rem; color: #666; }
        .emi-maxemi-val { font-size: 1.1rem; font-weight: 800; color: #003566; }
        /* ── Right panel ── */
        .emi-right {}
        .emi-slider-group { margin-bottom: 22px; }
        .emi-slider-header { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 10px; }
        .emi-slider-lbl { font-size: 0.9rem; font-weight: 700; color: #333; }
        .emi-slider-val { font-size: 1rem; font-weight: 800; color: #003566; }
        .emi-slider-marks { display: flex; justify-content: space-between; font-size: 0.75rem; color: #aaa; margin-top: 6px; }
        input[type="range"].emi-range::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 22px; height: 22px; border-radius: 50%;
          background: #003566; cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,53,102,0.35);
          border: 3px solid #fff;
        }
        input[type="range"].emi-range::-moz-range-thumb {
          width: 22px; height: 22px; border-radius: 50%;
          background: #003566; cursor: pointer; border: 3px solid #fff;
          box-shadow: 0 2px 8px rgba(0,53,102,0.35);
        }
        /* Employment buttons */
        .emi-emp-row { display: flex; gap: 10px; margin-bottom: 20px; }
        .emi-emp-btn {
          flex: 1; padding: 12px 8px; border-radius: 10px; font-size: 0.88rem;
          font-weight: 700; cursor: pointer; transition: all 0.18s;
          border: 2px solid #e0e0e0; background: #fff; color: #666;
        }
        .emi-emp-btn.active { border-color: #003566; background: #f0f4ff; color: #003566; }
        .emi-income-group { margin-bottom: 16px; }
        .emi-income-label { font-size: 0.82rem; font-weight: 600; color: #555; margin-bottom: 8px; display: block; }
        .emi-income-input {
          width: 100%; padding: 12px 14px; border: 2px solid #e0e0e0;
          border-radius: 10px; font-size: 1rem; font-weight: 700; color: #003566;
          outline: none; transition: border-color 0.2s; box-sizing: border-box;
        }
        .emi-income-input:focus { border-color: #003566; }
      `}</style>

      <div className="emi-calc-card">
        <h2 className="emi-calc-title">Car Loan Calculator</h2>

        <div className="emi-tabs">
          <button
            className={`emi-tab ${activeTab === 'emi' ? 'active' : ''}`}
            onClick={() => setActiveTab('emi')}
          >
            EMI Calculator
          </button>
          <button
            className={`emi-tab ${activeTab === 'estimate' ? 'active' : ''}`}
            onClick={() => setActiveTab('estimate')}
          >
            Loan Estimate
          </button>
        </div>

        <div className="emi-body">
          {/* ── LEFT: Result Display ── */}
          <div className="emi-left">
            {activeTab === 'emi' ? (
              <>
                <div className="emi-monthly-label">Monthly EMI</div>
                <div className="emi-monthly-amount">{fmtINR(emi)}</div>
                <div className="emi-monthly-sub">per month for {duration} months</div>

                <div className="emi-donut-row">
                  <svg
                    className="emi-donut-svg"
                    width="160"
                    height="160"
                    viewBox="0 0 160 160"
                  >
                    {/* Background ring */}
                    <circle
                      cx={cx} cy={cy} r={R}
                      fill="none" stroke="#e8e8e8" strokeWidth="24"
                    />
                    {/* Interest arc */}
                    <circle
                      cx={cx} cy={cy} r={R}
                      fill="none" stroke="#FFD60A" strokeWidth="24"
                      strokeDasharray={`${interestDash} ${circumference}`}
                      strokeDashoffset={-principalDash}
                      transform={`rotate(-90 ${cx} ${cy})`}
                      strokeLinecap="round"
                    />
                    {/* Principal arc */}
                    <circle
                      cx={cx} cy={cy} r={R}
                      fill="none" stroke="#003566" strokeWidth="24"
                      strokeDasharray={`${principalDash} ${circumference}`}
                      strokeDashoffset={0}
                      transform={`rotate(-90 ${cx} ${cy})`}
                      strokeLinecap="round"
                    />
                    <circle cx={cx} cy={cy} r={R - 20} fill="white" />
                    <text x={cx} y={cy - 6} textAnchor="middle" fontSize="11" fill="#888">Total</text>
                    <text x={cx} y={cy + 10} textAnchor="middle" fontSize="12" fontWeight="800" fill="#003566">
                      {fmtINR(totalPayable).replace('₹', '₹')}
                    </text>
                  </svg>

                  <div className="emi-legend">
                    <div className="emi-legend-item">
                      <div className="emi-legend-dot-row">
                        <div className="emi-legend-dot" style={{ background: '#003566' }} />
                        <span className="emi-legend-label">Principal</span>
                      </div>
                      <div className="emi-legend-val">{fmtINR(loanAmount)}</div>
                    </div>
                    <div className="emi-legend-item">
                      <div className="emi-legend-dot-row">
                        <div className="emi-legend-dot" style={{ background: '#FFD60A' }} />
                        <span className="emi-legend-label">Interest</span>
                      </div>
                      <div className="emi-legend-val">{fmtINR(totalInterest)}</div>
                    </div>
                    <div className="emi-legend-item">
                      <div className="emi-legend-dot-row">
                        <div className="emi-legend-dot" style={{ background: '#e0e7ff' }} />
                        <span className="emi-legend-label">Total Payable</span>
                      </div>
                      <div className="emi-legend-val">{fmtINR(totalPayable)}</div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="emi-maxloan-badge">
                  <div className="emi-maxloan-amount">{fmtINR(maxLoanAmount)}</div>
                  <div className="emi-maxloan-label">Max Loan Amount</div>
                </div>
                <div className="emi-maxemi-row">
                  <span className="emi-maxemi-lbl">Max Monthly EMI</span>
                  <span className="emi-maxemi-val">{fmtINR(maxEMI)}/mo</span>
                </div>
              </>
            )}
            <div className="emi-disclaimer">
              <strong>Disclaimer:</strong> Rate of interest varies by credit profile.
              Loan approval is at the sole discretion of the finance partner.
            </div>
          </div>

          {/* ── RIGHT: Inputs ── */}
          <div className="emi-right">
            {activeTab === 'estimate' && (
              <>
                <div className="emi-emp-row">
                  <button
                    className={`emi-emp-btn ${employmentType === 'salaried' ? 'active' : ''}`}
                    onClick={() => setEmploymentType('salaried')}
                  >
                    💼 Salaried
                  </button>
                  <button
                    className={`emi-emp-btn ${employmentType === 'self-employed' ? 'active' : ''}`}
                    onClick={() => setEmploymentType('self-employed')}
                  >
                    🏢 Self-Employed
                  </button>
                </div>

                {employmentType === 'salaried' ? (
                  <div className="emi-income-group">
                    <label className="emi-income-label">Monthly Income (₹)</label>
                    <input
                      type="number"
                      className="emi-income-input"
                      value={monthlyIncome}
                      onChange={(e) => setMonthlyIncome(Number(e.target.value))}
                    />
                  </div>
                ) : (
                  <div className="emi-income-group">
                    <label className="emi-income-label">Annual ITR Value (₹)</label>
                    <input
                      type="number"
                      className="emi-income-input"
                      value={annualITR}
                      onChange={(e) => setAnnualITR(Number(e.target.value))}
                    />
                  </div>
                )}

                <div className="emi-income-group">
                  <label className="emi-income-label">Already Running EMI (₹) — Optional</label>
                  <input
                    type="number"
                    className="emi-income-input"
                    placeholder="0"
                    value={runningEMI}
                    onChange={(e) => setRunningEMI(e.target.value)}
                  />
                </div>
              </>
            )}

            {activeTab === 'emi' && (
              <div className="emi-slider-group">
                <div className="emi-slider-header">
                  <span className="emi-slider-lbl">Loan Amount</span>
                  <span className="emi-slider-val">{fmtINR(loanAmount)}</span>
                </div>
                <input
                  type="range"
                  className="emi-range"
                  min={50000} max={5000000} step={10000}
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  style={sliderStyle(loanAmount, 50000, 5000000)}
                />
                <div className="emi-slider-marks">
                  <span>₹50K</span><span>₹50L</span>
                </div>
              </div>
            )}

            {/* Duration */}
            <div className="emi-slider-group">
              <div className="emi-slider-header">
                <span className="emi-slider-lbl">Loan Duration</span>
                <span className="emi-slider-val">{duration} months</span>
              </div>
              <input
                type="range"
                className="emi-range"
                min={12} max={84} step={6}
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                style={sliderStyle(duration, 12, 84)}
              />
              <div className="emi-slider-marks">
                <span>12 mo</span><span>84 mo</span>
              </div>
            </div>

            {/* Interest Rate */}
            <div className="emi-slider-group">
              <div className="emi-slider-header">
                <span className="emi-slider-lbl">Interest Rate</span>
                <span className="emi-slider-val">{interestRate}% p.a.</span>
              </div>
              <input
                type="range"
                className="emi-range"
                min={5} max={20} step={0.5}
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                style={sliderStyle(interestRate, 5, 20)}
              />
              <div className="emi-slider-marks">
                <span>5%</span><span>20%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EMICalculator;